import { defineComponent, ref, onMounted, computed, watch, } from '@vue/composition-api';
import { getSurveyUsers, sendSurveyRejectedNotification, SurveyUserStatus, getUserSurveysList, QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
import { useExcelExport } from '@/composables/use-excel-export';
import { aggregateItemKey, aggregateItemValue, parseAggregateAnswer, } from '@/views/Brand/Surveys/_helpers/aggregate-export';
import { useAuthUser } from '@/composables/use-auth-user';
import { useRoleChecker } from '@/composables/use-role-checker';
import { useClient } from '@/composables/use-client';
import { useBrand } from '@/composables/use-brand';
import TablePaginationV2 from '@/views/_components/Table/TablePaginationV2.vue';
import TableLoader from '@/views/_components/Table/TableLoader.vue';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import FloatingSelectFilter from '@/components/Table/AGTable/_components/FloatingSelectFilter.vue';
import SurveyAnsweredBadge from '@/views/Brand/Surveys/Users/_components/SurveyAnsweredBadge.vue';
import { useSurveyUsersAnswersColumns } from '@/views/Brand/Surveys/_composables/use-survey-users-answers-columns';
import { useTableQueryParams } from '@/components/Table/_composables/use-table-query-params';
import FloatingDatepickerFiler from '@/components/Table/AGTable/_components/FloatingDatepickerFilter.vue';
import SurveyAnswerCell from '@/views/Brand/Surveys/Users/_components/SurveyAnswerCell.vue';
import SurveyImageToggleFilter from '@/views/Brand/Surveys/Users/_components/SurveyImageToggleFilter.vue';
import { Icon, IconSize } from '@/components/Icon';
import { Popover } from '@/components/Popover';
import ShowingMenu from '@/components/EllipsisMenu/ShowingMenu.vue';
import { mdiDotsVertical } from '@mdi/js';
import { SystemRole } from '@/api/requests/users';
import SurveyAnswersAcceptanceModal from '@/layouts/_components/Surveys/SurveyAnswersAcceptanceModal.vue';
export default defineComponent({
    name: 'SurveyUsersAnswersList',
    components: {
        TablePaginationV2,
        TableLoader,
        Icon,
        Popover,
        ShowingMenu,
        SurveyAnswersAcceptanceModal,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingSelectFilter,
        // eslint-disable-next-line vue/no-unused-components
        SurveyAnsweredBadge,
        // eslint-disable-next-line vue/no-unused-components
        FloatingDatepickerFiler,
        // eslint-disable-next-line vue/no-unused-components
        SurveyAnswerCell,
        // eslint-disable-next-line vue/no-unused-components
        SurveyImageToggleFilter,
        AgTable: GenericAGTable(),
    },
    props: {
        surveyId: {
            type: String,
            required: true,
        },
        survey: {
            type: Object,
            default: null,
        },
        filterByCurrentUser: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, ctx) {
        const isLoadingResponses = ref(false);
        const responses = ref([]);
        const responsesTotalCount = ref(0);
        const agTableRef = ref(null);
        const selectedResponses = ref([]);
        const showSurveyModal = ref(false);
        const selectedSurvey = ref(null);
        const selectedPlace = ref(null);
        const selectedSurveyUserId = ref(null);
        const selectedStatuses = ref(null);
        const selectedUserFullName = ref(null);
        const isLoadingSurvey = ref(false);
        const generateQuestionFilterParams = (questions) => {
            const filterParams = {};
            questions.forEach((_, index) => {
                filterParams[`question-${index}`] = `question-${index}`;
            });
            return filterParams;
        };
        const generateQuestionFieldMap = (questions) => {
            const fieldMap = {};
            questions.forEach((_, index) => {
                fieldMap[`answers_${index}`] = `question-${index}`;
            });
            return fieldMap;
        };
        const { page: responsesPage, perPage: responsesPerPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useTableQueryParams(ctx, {
            defaultPerPage: 25,
            filterParams: {
                placeNumber: 'placeNumber',
                placeName: 'placeName',
                fullName: 'fullName',
                publishedAt: 'publishedAt',
                isAnswered: 'isAnswered',
                ...generateQuestionFilterParams(props.survey?.questions || []),
            },
            fieldMap: {
                'user.place.number': 'placeNumber',
                'user.place.name': 'placeName',
                'user.fullName': 'fullName',
                publishedAt: 'publishedAt',
                answers: 'isAnswered',
                ...generateQuestionFieldMap(props.survey?.questions || []),
            },
        });
        const responsesQuery = computed(() => {
            return {
                ...tableQuery.value,
                isAnswered: 'true',
            };
        });
        const { hasRole } = useAuthUser();
        const { client } = useClient();
        const { brand } = useBrand();
        const { checkAnyRole } = useRoleChecker(ctx);
        const canManageStatus = checkAnyRole([
            SystemRole.SUPER_ADMIN,
            SystemRole.STRUCTURE_MANAGER,
            SystemRole.BRAND_ADMIN,
        ]);
        const canSelectUsers = computed(() => {
            return (hasRole(SystemRole.SUPER_ADMIN) ||
                hasRole(SystemRole.BRAND_ADMIN) ||
                hasRole(SystemRole.STRUCTURE_MANAGER));
        });
        const isRowSelectable = (node) => {
            if (!canSelectUsers.value)
                return false;
            return Boolean(node.data);
        };
        const menuActions = computed(() => ({
            exportAll: {
                name: ctx.root.$t('brand-surveys.export.export-all'),
                handler: handleExportAll,
            },
            exportSelected: {
                name: ctx.root.$t('brand-surveys.export.export-selected', {
                    count: selectedResponses.value.length,
                }),
                handler: handleExportSelected,
                disabled: selectedResponses.value.length === 0,
            },
            notifyRejected: {
                name: ctx.root.$t('brand-surveys.answers.notify-rejected'),
                handler: notifyRejectedToSelected,
                disabled: !selectedResponses.value.some((r) => r.statuses?.some((s) => s === SurveyUserStatus.REJECTED)),
            },
        }));
        const fetchResponses = async () => {
            if (!props.surveyId || isLoadingResponses.value)
                return;
            const apiQuery = responsesQuery.value;
            isLoadingResponses.value = true;
            try {
                const result = await getSurveyUsers(props.surveyId, apiQuery);
                responses.value = result.nodes;
                responsesTotalCount.value = result.totalCount;
            }
            catch (error) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey-users'));
            }
            finally {
                isLoadingResponses.value = false;
            }
        };
        const changeResponsesPage = (page) => {
            responsesPage.value = page;
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sort) => {
            handleSort(field, sort);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const onSelectionChanged = (selectedRows) => {
            selectedResponses.value = selectedRows;
        };
        const clearSelection = () => {
            selectedResponses.value = [];
            if (agTableRef.value?.gridApi) {
                agTableRef.value.gridApi.deselectAll();
            }
        };
        const notifyRejectedToSelected = async () => {
            if (selectedResponses.value.length === 0)
                return;
            try {
                const rejectedResponses = selectedResponses.value.filter((response) => response.statuses?.some((status) => status === SurveyUserStatus.REJECTED));
                const users = rejectedResponses
                    .map((response) => ({
                    userId: response.user?.id || '',
                    placeId: response.user?.place?.id || '',
                }))
                    .filter((u) => u.userId && u.placeId);
                if (users.length === 0) {
                    ctx.root.$toast.error(ctx.root.$t('brand-surveys.users.no-valid-users-selected'));
                    return;
                }
                await sendSurveyRejectedNotification(props.surveyId, users);
                ctx.root.$toast.success(ctx.root.$t('brand-surveys.answers.survey-rejected-notification-sent', {
                    count: users.length,
                }));
                await fetchResponses();
            }
            catch (error) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.users.notification-send-error'));
            }
            finally {
                clearSelection();
            }
        };
        const fillSurveyWithAnswers = (mySurvey) => {
            const surveyWithAnswers = {
                ...mySurvey.survey,
                questions: mySurvey.survey.questions.map((question, idx) => {
                    const answer = mySurvey.answers?.[idx] || '';
                    const file = mySurvey.files?.[idx] || null;
                    const comment = mySurvey.comments?.[idx] || null;
                    if (question.type === QuestionType.CHECKBOX) {
                        const selectedOptions = answer
                            ? answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean)
                            : [];
                        return {
                            ...question,
                            value: answer,
                            attachmentFile: file,
                            comment,
                            options: question.options?.map((option) => ({
                                ...option,
                                isSelected: selectedOptions.includes(option.label),
                            })),
                        };
                    }
                    else if (question.type === QuestionType.RADIO) {
                        return {
                            ...question,
                            value: answer,
                            attachmentFile: file,
                            comment,
                            options: question.options?.map((option) => ({
                                ...option,
                                isSelected: option.label === answer,
                            })),
                        };
                    }
                    else {
                        return {
                            ...question,
                            value: answer,
                            attachmentFile: file,
                            comment,
                        };
                    }
                }),
            };
            return surveyWithAnswers;
        };
        const handlePlaceClick = async (row) => {
            if (!row.user?.place?.id || !row.user?.id) {
                ctx.root.$toast.error('Brak danych placówki lub użytkownika');
                return;
            }
            isLoadingSurvey.value = true;
            try {
                selectedUserFullName.value = row.user?.fullName || null;
                const { nodes } = await getUserSurveysList({
                    surveyId: props.surveyId,
                    placeId: row.user.place.id,
                    userId: row.user.id,
                    perPage: 1,
                    page: 1,
                });
                if (nodes.length > 0) {
                    const mySurvey = nodes[0];
                    selectedSurvey.value = fillSurveyWithAnswers(mySurvey);
                    selectedPlace.value = mySurvey.place;
                    selectedSurveyUserId.value = mySurvey.id;
                    selectedStatuses.value = mySurvey.statuses || [];
                    showSurveyModal.value = true;
                }
                else {
                    ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey'));
                }
            }
            catch (error) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey'));
            }
            finally {
                isLoadingSurvey.value = false;
            }
        };
        const handleStatusChange = async (questionIndex, newStatus) => {
            if (selectedStatuses.value) {
                const updatedStatuses = [...selectedStatuses.value];
                updatedStatuses[questionIndex] = newStatus;
                selectedStatuses.value = updatedStatuses;
            }
            await fetchResponses();
        };
        const closeSurveyModal = () => {
            showSurveyModal.value = false;
            selectedSurvey.value = null;
            selectedPlace.value = null;
            selectedSurveyUserId.value = null;
            selectedStatuses.value = null;
            selectedUserFullName.value = null;
        };
        const xlsxColumns = computed(() => {
            const result = [
                {
                    header: ctx.root.$t('brand-surveys.export.client'),
                    key: 'clientName',
                    width: 20,
                },
                {
                    header: ctx.root.$t('brand-surveys.export.brand'),
                    key: 'brandName',
                    width: 20,
                },
                {
                    header: ctx.root.$t('brand-surveys.export.user-id'),
                    key: 'userId',
                    width: 36,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.place-number'),
                    key: 'placeNumber',
                    width: 15,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.place-name'),
                    key: 'placeName',
                    width: 25,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.full-name'),
                    key: 'fullName',
                    width: 25,
                },
            ];
            const questions = props.survey?.questions || [];
            for (const [idx, question] of questions.entries()) {
                if (question.type === QuestionType.AGGREGATE) {
                    const items = question.items ?? [];
                    for (const [itemIdx, item] of items.entries()) {
                        result.push({
                            header: `${question.name} - ${item.label}`,
                            key: aggregateItemKey(idx, itemIdx),
                            width: 30,
                        });
                    }
                }
                else {
                    result.push({ header: question.name, key: `q${idx}`, width: 50 });
                }
                result.push({
                    header: `${question.name} - ${ctx.root.$t('brand-surveys.export.file')}`,
                    key: `q${idx}_file`,
                    width: 50,
                }, {
                    header: `${question.name} - ${ctx.root.$t('brand-surveys.export.comment')}`,
                    key: `q${idx}_comment`,
                    width: 50,
                });
            }
            return result;
        });
        const formatAnswer = (answer, question) => {
            if (!answer)
                return '';
            switch (question.type) {
                case QuestionType.CHECKBOX:
                    return answer
                        .split(SURVEY_ANSWER_SEPARATOR)
                        .filter(Boolean)
                        .join(', ');
                case QuestionType.FILE:
                    return answer
                        .split(SURVEY_ANSWER_SEPARATOR)
                        .filter(Boolean)
                        .join(', ');
                default:
                    return answer;
            }
        };
        const { handleExport, isExporting, saveProgress } = useExcelExport({
            columns: xlsxColumns.value,
            fileNamePrefix: 'survey-answers-export',
            dataMapper: (node) => {
                const result = {
                    clientName: client.value?.name || '',
                    brandName: brand.value?.name || '',
                    userId: node.user?.id || '',
                    placeNumber: node.user?.place?.number || '',
                    placeName: node.user?.place?.name || '',
                    fullName: node.user?.fullName || '',
                };
                const questions = props.survey?.questions || [];
                for (const [idx, question] of questions.entries()) {
                    const rawAnswer = node.answers?.[idx] || '';
                    if (question.type === QuestionType.AGGREGATE) {
                        const parsed = parseAggregateAnswer(rawAnswer);
                        const items = question.items ?? [];
                        for (const [itemIdx, item] of items.entries()) {
                            result[aggregateItemKey(idx, itemIdx)] = aggregateItemValue(parsed[item.label], item.inputType);
                        }
                    }
                    else {
                        result[`q${idx}`] = formatAnswer(rawAnswer, question);
                    }
                    result[`q${idx}_file`] = node?.files?.[idx] ?? '';
                    result[`q${idx}_comment`] = node?.comments?.[idx] ?? '';
                }
                return result;
            },
        });
        const handleExportAll = () => {
            const currentQuery = responsesQuery.value;
            const dataGetter = async ({ page, perPage, }) => {
                const result = await getSurveyUsers(props.surveyId, {
                    ...currentQuery,
                    sort: currentQuery.sort || '-createdAt',
                    page: Number(page),
                    perPage: Number(perPage),
                });
                return { totalCount: result.totalCount, nodes: result.nodes };
            };
            handleExport(dataGetter);
        };
        const handleExportSelected = () => {
            if (selectedResponses.value.length === 0)
                return;
            const selected = [...selectedResponses.value];
            const dataGetter = async () => {
                return { totalCount: selected.length, nodes: selected };
            };
            handleExport(dataGetter);
        };
        watch(() => props.surveyId, () => {
            if (props.surveyId) {
                clearSelection();
                fetchResponses();
            }
        }, { immediate: true });
        watch(responsesQuery, () => {
            fetchResponses();
        }, { deep: true });
        onMounted(() => {
            if (props.surveyId) {
                fetchResponses();
            }
        });
        const { columns } = useSurveyUsersAnswersColumns(ctx, props.survey?.questions || [], props.survey?.internalNumber, fetchResponses, canManageStatus ? handlePlaceClick : undefined);
        return {
            responsesQuery,
            responsesPage,
            responsesPerPage,
            isLoadingResponses,
            responses,
            responsesTotalCount,
            selectedResponses,
            menuActions,
            changeResponsesPage,
            columns,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            onSelectionChanged,
            clearSelection,
            notifyRejectedToSelected,
            agTableRef,
            mdiDotsVertical,
            IconSize,
            isRowSelectable,
            canSelectUsers,
            canManageStatus,
            showSurveyModal,
            selectedSurvey,
            selectedPlace,
            selectedUserFullName,
            selectedSurveyUserId,
            selectedStatuses,
            isLoadingSurvey,
            closeSurveyModal,
            handlePlaceClick,
            handleStatusChange,
            isExporting,
            saveProgress,
        };
    },
});
